/**
 * byo-v4-pack.js — the add-to-cart packing scene (P6).
 *
 * Ported from `byo-tissue.jsx` (whole file): `tissueTiming`, `TissueSheet`,
 * `TissueScene`. DESIGN-SPEC.md §6.
 *
 * Timings are the design's, to the frame (`tissueTiming`):
 *   items .2s at .16s stagger · gift card 1.0s · left tissue 1.45s ·
 *   right 1.80s · lime sticker 2.55s · parcel fires 3.05s · lands 3.45s.
 * **Quick mode** — any box after the first in a session — has the items and the
 * card already in, and the fold starts at .2s.
 *
 * There is no "Adding…" overlay. The scene *is* the feedback (BUILD-PLAN P6).
 *
 * Tapping anywhere skips to the lime tick, and `prefers-reduced-motion` shows
 * that tick immediately — the scene is `aria-hidden`, so nothing is lost.
 *
 * ES2020 module. View layer: assets/byo-v4-vendor.js (Preact + htm).
 */

import { html, useState, useEffect } from './byo-v4-vendor.js';
const { Icon, ProductImage } = (window.BYOV4 = window.BYOV4 || {});
import './byo-v4-brand-marks.js';

/** tissueTiming — byo-tissue.jsx:6, verbatim. Seconds. */
export function tissueTiming(quick) {
  const itemAt = quick ? 0 : 0.2;
  const cardAt = quick ? 0 : 1.0;
  const foldL = quick ? 0.2 : 1.45;
  const foldR = foldL + 0.35;
  const sealAt = foldR + 0.75;
  const shipAt = sealAt + 0.5;
  const landAt = shipAt + 0.4;
  return { itemAt, cardAt, foldL, foldR, sealAt, shipAt, landAt };
}

const wSrc = () => window.WRAPT_W_SRC || '';
const logoSrc = () => window.WRAPT_LOGO_SRC || '';

/** The twelve rotated W marks on a tissue sheet — byo-tissue.jsx:9. */
const W_ROTATIONS = [-26, 16, -6, 30, -16, 22];

function TissueSheet({ side, anim }) {
  return html`
    <div class=${`byo-v4-tis__sheet byo-v4-tis__sheet--${side}`} style=${`animation:${anim}`}>
      ${Array.from({ length: 12 }, (_, i) => {
        const r = i % 3;
        const c = (i / 3) | 0;
        const left = 10 + r * 42 + (c % 2 ? 20 : 0);
        const top = 14 + c * 42;
        const rot = W_ROTATIONS[(i * 5) % 6];
        return html`<img
          key=${i}
          class="byo-v4-tis__w"
          src=${wSrc()}
          alt=""
          style=${`left:${left}px; top:${top}px; transform:rotate(${rot}deg)`}
        />`;
      })}
    </div>
  `;
}

/**
 * TissueScene — byo-tissue.jsx:12. Products nest in the black shred, the gift
 * card lands, branded tissue folds in from both sides, the lime sticker stamps,
 * and the parcel fires up towards the header bag.
 *
 * `onDone` fires at `landAt` so the caller can pulse the bag badge as the
 * parcel arrives rather than when the request returned.
 */
export function TissueScene({ units, quick, onDone, onSkip }) {
  const t = tissueTiming(quick);
  const shown = (units || []).slice(0, 4);

  const reduced =
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const [skipped, setSkipped] = useState(reduced);

  useEffect(() => {
    if (skipped) {
      onDone && onDone();
      return undefined;
    }
    const id = setTimeout(() => onDone && onDone(), t.landAt * 1000);
    return () => clearTimeout(id);
  }, [skipped]);

  const skip = () => {
    if (skipped) return;
    setSkipped(true);
    onSkip && onSkip();
  };

  if (skipped) {
    return html`
      <div class="byo-v4-tis byo-v4-tis--tick" aria-hidden="true">
        <span class="byo-v4-tis__tick"><${Icon} name="check" size=${34} stroke=${3} /></span>
      </div>
    `;
  }

  return html`
    <div class="byo-v4-tis" aria-hidden="true" onClick=${skip}>
      <div class="byo-v4-tis__panel" style=${`animation: byo-tis-fire .55s cubic-bezier(.4,0,.7,.3) ${t.shipAt}s both`}>
        <div class="byo-v4-tis__items">
          ${shown.map(
            (u, i) => html`<div
              key=${u.key}
              class="byo-v4-tis__item"
              style=${quick
                ? ''
                : `animation: byo-tis-drop .5s cubic-bezier(.3,.9,.3,1.1) ${(t.itemAt + i * 0.16).toFixed(2)}s both`}
            >
              <${ProductImage} p=${u.p} fit="contain" />
            </div>`
          )}
        </div>

        <div
          class="byo-v4-tis__card"
          style=${quick ? '' : `animation: byo-tis-drop .5s cubic-bezier(.3,.9,.3,1.1) ${t.cardAt}s both`}
        >
          <img class="byo-v4-tis__cardw" src=${wSrc()} alt="" />
        </div>

        <${TissueSheet} side="left" anim=${`byo-tis-foldL .75s cubic-bezier(.35,.7,.25,1) ${t.foldL}s both`} />
        <${TissueSheet} side="right" anim=${`byo-tis-foldR .75s cubic-bezier(.35,.7,.25,1) ${t.foldR}s both`} />

        <div
          class="byo-v4-tis__seal"
          style=${`animation: byo-tis-stamp .45s cubic-bezier(.2,.9,.3,1.25) ${t.sealAt}s both`}
        >
          <img class="byo-v4-tis__logo" src=${logoSrc()} alt="Wrapt" />
        </div>
      </div>
    </div>
  `;
}

/**
 * The header bag badge pulse, delayed to `landAt` so the +1 lands as the parcel
 * arrives (DESIGN-SPEC.md §3.4 / §6). The theme owns the bag, so this only
 * decorates whatever bag icon the header already renders; it never dispatches
 * the theme's own cart events, which mutate the cart (GIFTSHIP-CONTRACT.md).
 */
export function pulseHeaderBag() {
  const bag = document.querySelector('[href$="/cart"] svg, .header__icon--cart, [data-cart-icon]');
  if (!bag || !bag.animate) return;
  bag.animate(
    [{ transform: 'none' }, { transform: 'translateY(-6px) scale(1.15)' }, { transform: 'translateY(2px)' }, { transform: 'none' }],
    { duration: 520, easing: 'ease-out' }
  );
}

/* Published for the siblings that used to `import` this file. See the
   section's loader: relative imports drop the ?v= and are served from a
   year-long CDN cache, so a sibling could be a build behind. */
Object.assign((window.BYOV4 = window.BYOV4 || {}), { TissueScene, pulseHeaderBag, tissueTiming });
